import type { ClientOrRelativesRequest, ClientOrRelativesResponse } from 'apiConnector/secondary-market/shared/clients';
import type { SettlementDateResponse } from 'apiConnector/secondary-market/shared/settlement';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { TradeDateType } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import { ClientType } from 'state/secondaryMarket/shared/form.models';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import type { IHttpClient } from 'utils/http/httpClient.model';

import type {
  CpOrderGetOrderResponse,
  CpOrderReloadFormRequest,
  CpOrderReloadFormResponse,
  CpOrderResponse,
  LinkedToClientResponse,
  ModifyClientRequest,
  PlacedOrderResponse,
  PlaceOrderRequest,
  PreviousReloadFormResponse,
  ReloadFormRequest,
} from './model';

export interface CpOrderGetOrderByIdApiV2 {
  getOrderByIdV2(orderId: string): Observable<CpOrderResponse>;
}

export interface CpOrderInitFormApi {
  init(request: CpOrderReloadFormRequest): Observable<CpOrderReloadFormResponse>;
}

export interface CpOrderClientFormApi {
  interactClient(request: ClientOrRelativesRequest): Observable<ClientOrRelativesResponse>;
}

export interface CpOrderReloadFormApi {
  reload(request: ReloadFormRequest): Observable<PreviousReloadFormResponse>;
}

export interface CpOrderFormClientApi {
  getReferentialLinkedToClient(data: ModifyClientRequest): Observable<LinkedToClientResponse>;
}

export interface CpOrderSettlementDateApi {
  getSettlementDate(
    key: BcsProductKey,
    valuationDate: string,
    cobDate: string | undefined,
    tradeDateType: TradeDateType | undefined,
    counterpartyEliotId: string | undefined,
    clientType: ClientType | undefined,
  ): Observable<SettlementDateResponse>;
}

export interface CpOrderPlacementApi {
  placeOrder(request: PlaceOrderRequest): Observable<PlacedOrderResponse>;
}

export interface CpOrderAmendApi {
  amendOrder(orderId: string, etag: string, request: Partial<PlaceOrderRequest>): Observable<PlacedOrderResponse>;
}

export interface CpOrderGetOrderByIdApi {
  getOrderById(orderId: string): Observable<CpOrderGetOrderResponse>;
}

export function cpOrderFormApiConnector(
  httpClient: IHttpClient,
): CpOrderReloadFormApi &
  CpOrderFormClientApi &
  CpOrderSettlementDateApi &
  CpOrderPlacementApi &
  CpOrderAmendApi &
  CpOrderGetOrderByIdApi &
  CpOrderGetOrderByIdApiV2 &
  CpOrderInitFormApi &
  CpOrderClientFormApi {
  return {
    reload(request) {
      return httpClient
        .postJSON<PreviousReloadFormResponse>({
          url: `api/v1/secondary-market/cp-order/forms/init`,
          body: request,
          tryParseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    getReferentialLinkedToClient(request) {
      return httpClient
        .postJSON<LinkedToClientResponse>({
          url: `api/v1/secondary-market/cp-order/client`,
          body: request,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    getSettlementDate(key, valuationDate, cobDate, tradeDateType, counterpartyEliotId, clientType) {
      return httpClient
        .getJSON<SettlementDateResponse>({
          url: `api/v1/secondary-market/cp-order/settlement-date`,
          params: {
            bcsSource: key.source,
            bcsSubType: key.subType,
            bcsCode: key.code,
            valuationDate,
            cobDate,
            tradeDateType,
            counterpartyEliotId,
            clientType,
          },
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    placeOrder(request) {
      return httpClient
        .postJSON<PlacedOrderResponse>({
          url: `api/v1/secondary-market/cp-order/placement`,
          body: request,
          timeOut: 10_000,
          tryParseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    amendOrder(orderId, etag, request) {
      return httpClient
        .postJSON<PlacedOrderResponse>({
          url: `api/v1/secondary-market/cp-order/amend?orderId=${encodeURIComponent(orderId)}&etag=${encodeURIComponent(
            etag,
          )}`,
          body: request,
          tryParseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    getOrderById(orderId) {
      return httpClient
        .getJSON<CpOrderGetOrderResponse>({
          url: `api/v1/secondary-market/cp-order/order?orderId=${encodeURIComponent(orderId)}`,
          tryParseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    getOrderByIdV2: (orderId: string) => {
      return httpClient
        .getJSON<CpOrderResponse>({
          url: `api/v2/secondary-market/cp-order?orderId=${encodeURIComponent(orderId)}`,
          tryParseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    init: request => {
      return httpClient
        .postJSON<CpOrderReloadFormResponse>({
          url: 'api/v1/secondary-market/cp-order/forms/init',
          body: request,
          tryParseErrorPayload: true,
          parseErrorPayload: true,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
    interactClient: request => {
      return httpClient
        .postJSON<ClientOrRelativesResponse>({
          url: 'api/v1/secondary-market/cp-order/client',
          body: request,
          targetHost: 'SecondaryBridge',
        })
        .pipe(map(({ payload }) => payload));
    },
  };
}
